import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import { ArrowLeftIcon, Edit2Icon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledTable from '@/components/styled/StyledTable';
import {
  Estimate,
  ESTIMATES_COLUMN_DEF,
} from '@/types/estimates/estimatesColumnDef';
import Loader from '@/components/ui/Loader';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import CreateEstimateModal from '@/components/estimates/CreateEstimateModal';
import { useDeleteEstimate, useGetEstimates } from '@/hooks/useEstimate';
import { EstimatesResponse } from '@/api/estimate.api';
import { differenceInYears } from 'date-fns';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import ViewProjectModal from '@/components/projects/ViewProjectModal';
import { useToast } from '@/hooks/useToast';
import ModalDeletion from '@/components/ui/ModalDeletion';
import DuplicateEstimateModal from '@/components/estimates/DuplicateEstimateModal';
import useTableControls from '@/hooks/useTableControls';
import { PAGE_SIZE } from '@/constants/pageSize';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import { PATHS } from '@/constants/Paths';
import { Row, SortingState } from '@tanstack/react-table';
import UpdateEstimateInfoModal from '@/components/estimates/UpdateEstimateInfoModal';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';

const ListEstimatesPage = () => {
  const { projectId } = useParams();

  const { page, setPage, sortField, sortOrder, setSorting, sorting } =
    useTableControls();

  const {
    data: estimates,
    isLoading,
    isSuccess,
  } = useGetEstimates(projectId ?? '', {
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
  });

  return !projectId || isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <EstimatesList
        estimateResponse={estimates}
        page={page}
        setPage={setPage}
        sorting={sorting}
        setSorting={setSorting}
        key={`estimate-${projectId}`}
      />
    )
  );
};

export default ListEstimatesPage;

interface EstimatesListProps {
  estimateResponse: EstimatesResponse;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
}

const EstimatesList = ({
  estimateResponse,
  page,
  setPage,
  sorting,
  setSorting,
}: EstimatesListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isTourOperator } = useDistributorLabels();
  const getTranslatedConst = useTranslatedConst();
  const { mutateAsync: deleteEstimate } = useDeleteEstimate(
    estimateResponse.project.id,
  );
  const { toastAsync } = useToast();

  const [isEstimateCreationModalOpen, setIsEstimateCreationModalOpen] =
    useState(false);
  const [estimateToDeleteId, setEstimateToDeleteId] = useState<string | null>(
    null,
  );
  const [estimateToDuplicateId, setEstimateToDuplicateId] = useState<
    string | null
  >(null);
  const [estimateToUpdateId, setEstimateToUpdateId] = useState<string | null>(
    null,
  );

  const projectDisplayName = useMemo(() => {
    const project = estimateResponse.project;
    return project.clientFirstName
      ? `${project.name} ${project.clientFirstName}`
      : project.name;
  }, [estimateResponse.project]);

  const breadCrumbLabel = useMemo(() => {
    const client = estimateResponse.project.client;
    if (isTourOperator) {
      const agencyLabel =
        client.agencyName ??
        `${client.mainContact.firstName} ${client.mainContact.lastName}`;
      return `${agencyLabel} / ${projectDisplayName}`;
    }
    const { firstName, lastName } = client.mainContact;
    return `${firstName} ${lastName} / ${t('clients.projects')}`;
  }, [estimateResponse.project, isTourOperator, projectDisplayName, t]);

  const childrenBirthDates = useMemo(
    () =>
      estimateResponse.project.children.map(
        ({ birthDate }) => new Date(birthDate),
      ),
    [estimateResponse.project.children],
  );

  const goToEstimate = useCallback(
    (row: Row<Estimate>) => {
      navigate(
        PATHS.distributors.clients.estimates.view
          .replace(':clientId', estimateResponse.project.clientId)
          .replace(':projectId', estimateResponse.project.id)
          .replace(':estimateId', row.original.id),
      );
    },
    [estimateResponse.project.clientId, estimateResponse.project.id, navigate],
  );

  return (
    <StyledContainer>
      <StyledFormHeaderWithBreadcrumb
        title={
          isTourOperator ? projectDisplayName : estimateResponse.project.name
        }
        breadcrumb={
          <StyledButton
            variant="link"
            title={breadCrumbLabel}
            className="text-sm text-[#3D3D3D] pl-0"
            onClick={() =>
              navigate(
                PATHS.distributors.clients.view.replace(
                  ':clientId',
                  estimateResponse.project.clientId,
                ),
              )
            }
            icon={<ArrowLeftIcon />}
          />
        }
        underNode={
          <ParticipantsHeader
            countAdults={estimateResponse.project._count.adults}
            countChildren={estimateResponse.project._count.children}
            childrenDates={childrenBirthDates}
            clientId={estimateResponse.project.clientId}
            projectId={estimateResponse.project.id}
            isTourOperator={isTourOperator}
          />
        }
      >
        <StyledButton
          title={t('estimates.createEstimate')}
          className="px-3 py-2"
          icon={<PlusIcon className="size-5 fill-white" />}
          onClick={() => {
            setIsEstimateCreationModalOpen(true);
          }}
        />
      </StyledFormHeaderWithBreadcrumb>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <StyledTable
          columns={getTranslatedConst(ESTIMATES_COLUMN_DEF)}
          data={estimateResponse.estimates}
          getRowId={(row) => row.id}
          onViewRow={goToEstimate}
          onRowClick={goToEstimate}
          onDuplicateRow={(row) => {
            setEstimateToDuplicateId(row.original.id);
          }}
          onDeleteRow={(row) => {
            setEstimateToDeleteId(row.original.id);
          }}
          onEditRow={(row) => {
            setEstimateToUpdateId(row.original.id);
          }}
          editEnablePredicate={(row) =>
            row.original.status === EstimateStatus.PENDING
          }
          enablePagination={false}
          sorting={sorting}
          setSorting={setSorting}
          alertMessage={t('estimates.alertMessage')}
          alertEnablePredicate={(row) => Boolean(row.original.autoUpdatedAt)}
        />
        <StyledNextPreviousButtons
          dataLength={estimateResponse.total}
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          label="Devis"
          pluralLabel="Devis"
        />
      </div>
      {isEstimateCreationModalOpen && (
        <CreateEstimateModal
          closeModal={() => {
            setIsEstimateCreationModalOpen(false);
          }}
          nbAdults={estimateResponse.project._count.adults}
          nbChildren={estimateResponse.project._count.children}
          clientId={estimateResponse.project.clientId}
          projectId={estimateResponse.project.id}
        />
      )}
      {estimateToDuplicateId && (
        <DuplicateEstimateModal
          closeModal={() => {
            setEstimateToDuplicateId(null);
          }}
          estimateId={estimateToDuplicateId}
          projectId={estimateResponse.project.id}
        />
      )}
      {estimateToDeleteId && (
        <ModalDeletion
          type="estimate"
          closeModal={() => {
            setEstimateToDeleteId(null);
          }}
          actionOnClick={async () => {
            await toastAsync(deleteEstimate(estimateToDeleteId), {
              success: t('estimates.deleteSuccess'),
            });
          }}
        />
      )}
      {estimateToUpdateId && (
        <UpdateEstimateInfoModal
          estimateId={estimateToUpdateId}
          projectId={estimateResponse.project.id}
          closeModal={() => {
            setEstimateToUpdateId(null);
          }}
        />
      )}
    </StyledContainer>
  );
};

interface ParticipantsHeaderProps {
  countAdults: number;
  countChildren: number;
  childrenDates: Date[];
  clientId: string;
  projectId: string;
  isTourOperator?: boolean;
}

const ParticipantsHeader = ({
  countAdults,
  countChildren,
  childrenDates,
  clientId,
  projectId,
  isTourOperator,
}: ParticipantsHeaderProps) => {
  const [isProjectEditModalOpen, setIsProjectEditModalOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="flex w-fit items-center gap-4">
      <span className="text-sm leading-6 font-medium text-gray-700">
        {t('clients.participants')}:{' '}
        {t('clients.participantsCount', {
          adults: countAdults,
          children: countChildren,
        })}{' '}
        (
        {childrenDates
          .slice(0, childrenDates.length > 1 ? -1 : undefined)
          .map((birthDate) =>
            t('clients.childrenAges', {
              ages: differenceInYears(new Date(), new Date(birthDate)),
            }),
          )
          .join(', ')}
        {childrenDates.length > 1
          ? ` ${t('clients.childrenAgesMultiple', {
              ages: childrenDates
                .slice(0, -1)
                .map((date) => differenceInYears(new Date(), date))
                .join(', '),
              lastAge: differenceInYears(
                new Date(),
                childrenDates[childrenDates.length - 1],
              ),
            })}`
          : null}
        )
      </span>
      <StyledTooltipContainer
        tooltipMessage={t('clients.editParticipants')}
        asChild
      >
        <Edit2Icon
          className="size-4 stroke-brand-500 hover:cursor-pointer"
          onClick={() => {
            setIsProjectEditModalOpen(true);
          }}
        />
      </StyledTooltipContainer>
      {isProjectEditModalOpen && (
        <ViewProjectModal
          clientId={clientId}
          projectId={projectId}
          isTourOperator={isTourOperator}
          closeModal={() => {
            setIsProjectEditModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
